// GraphQL Mutation Operations with TypeScript types
import type {
  Config_Param,
  CreateConfig_ParamInput,
  UpdateConfig_ParamInput,
  DeleteConfig_ParamInput,
  Data_Element,
  CreateData_ElementInput,
  UpdateData_ElementInput,
  DeleteData_ElementInput
} from '../generated/graphql'

/**
 * Usage example with generated types:
 * 
 * import { executeGraphQL } from '@/utils/unifiedGraphQLClient'
 * import { createConfigParam } from '@/graphql/mutations'
 * import type { Config_Param, CreateConfig_ParamInput } from '@/generated/graphql'
 * 
 * const input: CreateConfig_ParamInput = {
 *   CONFIG_NAME: 'MY_CONFIG',
 *   CONFIG_VALUE: 'value'
 * }
 * 
 * const result = await executeGraphQL<{ createCONFIG_PARAM: Config_Param }>(
 *   createConfigParam,
 *   { input }
 * )
 * 
 * // result.data.createCONFIG_PARAM is now fully typed!
 */

// Config Param mutations
export const createConfigParam = `
  mutation CreateConfigParam($input: CreateCONFIG_PARAMInput!) {
    createCONFIG_PARAM(input: $input) {
      CONFIG_PARAM_ID
      CONFIG_NAME
      CONFIG_VALUE
      DESCRIPTION
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const updateConfigParam = `
  mutation UpdateConfigParam($input: UpdateCONFIG_PARAMInput!) {
    updateCONFIG_PARAM(input: $input) {
      CONFIG_PARAM_ID
      CONFIG_NAME
      CONFIG_VALUE
      DESCRIPTION
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const deleteConfigParam = `
  mutation DeleteConfigParam($input: DeleteCONFIG_PARAMInput!) {
    deleteCONFIG_PARAM(input: $input) {
      CONFIG_PARAM_ID
    }
  }
`

// Data Element mutations
export const createDataElement = `
  mutation CreateDataElement($input: CreateDATA_ELEMENTInput!) {
    createDATA_ELEMENT(input: $input) {
      DATA_ELEMENT_ID
      ELEMENT_KEY
      ELEMENT_VALUE
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateDataElement = `
  mutation UpdateDataElement($input: UpdateDATA_ELEMENTInput!) {
    updateDATA_ELEMENT(input: $input) {
      DATA_ELEMENT_ID
      ELEMENT_KEY
      ELEMENT_VALUE
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteDataElement = `
  mutation DeleteDataElement($input: DeleteDATA_ELEMENTInput!) {
    deleteDATA_ELEMENT(input: $input) {
      DATA_ELEMENT_ID
    }
  }
`

// Filter Criteria mutations
export const createFilterCriteria = `
  mutation CreateFilterCriteria($input: CreateFILTER_CRITERIAInput!) {
    createFILTER_CRITERIA(input: $input) {
      FILTER_CRITERIA_ID
      ORIGIN_PRODUCT_ID
      STEP_TYPE_ID
      CRITERIA
      SEQUENCE_NBR
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const updateFilterCriteria = `
  mutation UpdateFilterCriteria($input: UpdateFILTER_CRITERIAInput!) {
    updateFILTER_CRITERIA(input: $input) {
      FILTER_CRITERIA_ID
      ORIGIN_PRODUCT_ID
      STEP_TYPE_ID
      CRITERIA
      SEQUENCE_NBR
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const deleteFilterCriteria = `
  mutation DeleteFilterCriteria($input: DeleteFILTER_CRITERIAInput!) {
    deleteFILTER_CRITERIA(input: $input) {
      FILTER_CRITERIA_ID
    }
  }
`

// Origin Product mutations
export const createOriginProduct = `
  mutation CreateOriginProduct($input: CreateORIGIN_PRODUCTInput!) {
    createORIGIN_PRODUCT(input: $input) {
      ORIGIN_PRODUCT_ID
      VENDOR_NAME
      PRODUCT_ID
      PSCU_CLIENT_ID
      PRODUCT_DESC
      PARTNER_CODE
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateOriginProduct = `
  mutation UpdateOriginProduct($input: UpdateORIGIN_PRODUCTInput!) {
    updateORIGIN_PRODUCT(input: $input) {
      ORIGIN_PRODUCT_ID
      VENDOR_NAME
      PRODUCT_ID
      PSCU_CLIENT_ID
      PRODUCT_DESC
      PARTNER_CODE
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteOriginProduct = `
  mutation DeleteOriginProduct($input: DeleteORIGIN_PRODUCTInput!) {
    deleteORIGIN_PRODUCT(input: $input) {
      ORIGIN_PRODUCT_ID
    }
  }
`

// Redirect URL mutations
export const createRedirectUrl = `
  mutation CreateRedirectUrl($input: CreateREDIRECT_URLInput!) {
    createREDIRECT_URL(input: $input) {
      REDIRECT_URL_ID
      ORIGIN_PRODUCT_ID
      URL_TYPE_CODE
      URL
      RESPONSE_TEXT
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateRedirectUrl = `
  mutation UpdateRedirectUrl($input: UpdateREDIRECT_URLInput!) {
    updateREDIRECT_URL(input: $input) {
      REDIRECT_URL_ID
      ORIGIN_PRODUCT_ID
      URL_TYPE_CODE
      URL
      RESPONSE_TEXT
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteRedirectUrl = `
  mutation DeleteRedirectUrl($input: DeleteREDIRECT_URLInput!) {
    deleteREDIRECT_URL(input: $input) {
      REDIRECT_URL_ID
    }
  }
`

// Service mutations
export const createService = `
  mutation CreateService($input: CreateSERVICEInput!) {
    createSERVICE(input: $input) {
      SERVICE_ID
      SERVICE_PROVIDER_ID
      URI
      SECRET_NAME
      REQUEST_TYPE
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateService = `
  mutation UpdateService($input: UpdateSERVICEInput!) {
    updateSERVICE(input: $input) {
      SERVICE_ID
      SERVICE_PROVIDER_ID
      URI
      SECRET_NAME
      REQUEST_TYPE
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteService = `
  mutation DeleteService($input: DeleteSERVICEInput!) {
    deleteSERVICE(input: $input) {
      SERVICE_ID
    }
  }
`

// Service Domain mutations
export const createServiceDomain = `
  mutation CreateServiceDomain($input: CreateSERVICE_DOMAINInput!) {
    createSERVICE_DOMAIN(input: $input) {
      SERVICE_DOMAIN_ID
      DOMAIN_URL
      SERVICE_PROVIDER_ID
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const updateServiceDomain = `
  mutation UpdateServiceDomain($input: UpdateSERVICE_DOMAINInput!) {
    updateSERVICE_DOMAIN(input: $input) {
      SERVICE_DOMAIN_ID
      DOMAIN_URL
      SERVICE_PROVIDER_ID
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const deleteServiceDomain = `
  mutation DeleteServiceDomain($input: DeleteSERVICE_DOMAINInput!) {
    deleteSERVICE_DOMAIN(input: $input) {
      SERVICE_DOMAIN_ID
    }
  }
`

// Service Expression Mapping mutations
export const createServiceExprMapping = `
  mutation CreateServiceExprMapping($input: CreateSERVICE_EXPR_MAPPINGInput!) {
    createSERVICE_EXPR_MAPPING(input: $input) {
      SERVICE_EXPR_MAPPING_ID
      SERVICE_PARAM_MAPPING_ID
      STEP_TYPE_ID
      SEQUENCE_NBR
      SOURCE_EXPR
      TARGET_EXPR
      COMMENT_TEXT
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateServiceExprMapping = `
  mutation UpdateServiceExprMapping($input: UpdateSERVICE_EXPR_MAPPINGInput!) {
    updateSERVICE_EXPR_MAPPING(input: $input) {
      SERVICE_EXPR_MAPPING_ID
      SERVICE_PARAM_MAPPING_ID
      STEP_TYPE_ID
      SEQUENCE_NBR
      SOURCE_EXPR
      TARGET_EXPR
      COMMENT_TEXT
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteServiceExprMapping = `
  mutation DeleteServiceExprMapping($input: DeleteSERVICE_EXPR_MAPPINGInput!) {
    deleteSERVICE_EXPR_MAPPING(input: $input) {
      SERVICE_EXPR_MAPPING_ID
    }
  }
`

// Service Param mutations
export const createServiceParam = `
  mutation CreateServiceParam($input: CreateSERVICE_PARAMInput!) {
    createSERVICE_PARAM(input: $input) {
      SERVICE_PARAM_ID
      SERVICE_ID
      PARAM_NAME
      IS_SENSITIVE
      DATA_ELEMENT_ID
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateServiceParam = `
  mutation UpdateServiceParam($input: UpdateSERVICE_PARAMInput!) {
    updateSERVICE_PARAM(input: $input) {
      SERVICE_PARAM_ID
      SERVICE_ID
      PARAM_NAME
      IS_SENSITIVE
      DATA_ELEMENT_ID
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteServiceParam = `
  mutation DeleteServiceParam($input: DeleteSERVICE_PARAMInput!) {
    deleteSERVICE_PARAM(input: $input) {
      SERVICE_PARAM_ID
    }
  }
`

// Service Param Mapping mutations
export const createServiceParamMapping = `
  mutation CreateServiceParamMapping($input: CreateSERVICE_PARAM_MAPPINGInput!) {
    createSERVICE_PARAM_MAPPING(input: $input) {
      SERVICE_PARAM_MAPPING_ID
      ORIGIN_PRODUCT_ID
      SYSTEM_NBR
      PRIN_NBR
      AGENT_NBR
      SOURCE_SERVICE_PARAM_ID
      TARGET_SERVICE_PARAM_ID
      PLASTIC_TYPE_ID
      COMMENT_TEXT
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateServiceParamMapping = `
  mutation UpdateServiceParamMapping($input: UpdateSERVICE_PARAM_MAPPINGInput!) {
    updateSERVICE_PARAM_MAPPING(input: $input) {
      SERVICE_PARAM_MAPPING_ID
      ORIGIN_PRODUCT_ID
      SYSTEM_NBR
      PRIN_NBR
      AGENT_NBR
      SOURCE_SERVICE_PARAM_ID
      TARGET_SERVICE_PARAM_ID
      PLASTIC_TYPE_ID
      COMMENT_TEXT
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteServiceParamMapping = `
  mutation DeleteServiceParamMapping($input: DeleteSERVICE_PARAM_MAPPINGInput!) {
    deleteSERVICE_PARAM_MAPPING(input: $input) {
      SERVICE_PARAM_MAPPING_ID
    }
  }
`

// Service Provider mutations
export const createServiceProvider = `
  mutation CreateServiceProvider($input: CreateSERVICE_PROVIDERInput!) {
    createSERVICE_PROVIDER(input: $input) {
      SERVICE_PROVIDER_ID
      SERVICE_PROVIDER_NAME
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateServiceProvider = `
  mutation UpdateServiceProvider($input: UpdateSERVICE_PROVIDERInput!) {
    updateSERVICE_PROVIDER(input: $input) {
      SERVICE_PROVIDER_ID
      SERVICE_PROVIDER_NAME
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteServiceProvider = `
  mutation DeleteServiceProvider($input: DeleteSERVICE_PROVIDERInput!) {
    deleteSERVICE_PROVIDER(input: $input) {
      SERVICE_PROVIDER_ID
    }
  }
`

// Service Response mutations
export const createServiceResponse = `
  mutation CreateServiceResponse($input: CreateSERVICE_RESPONSEInput!) {
    createSERVICE_RESPONSE(input: $input) {
      SERVICE_RESPONSE_ID
      SERVICE_ID
      RESPONSE_PATH
      DATA_TYPE
      IS_SENSITIVE
      STATUS_CODE_START
      STATUS_CODE_END
      DATA_ELEMENT_ID
    }
  }
`

export const updateServiceResponse = `
  mutation UpdateServiceResponse($input: UpdateSERVICE_RESPONSEInput!) {
    updateSERVICE_RESPONSE(input: $input) {
      SERVICE_RESPONSE_ID
      SERVICE_ID
      RESPONSE_PATH
      DATA_TYPE
      IS_SENSITIVE
      STATUS_CODE_START
      STATUS_CODE_END
      DATA_ELEMENT_ID
    }
  }
`

export const deleteServiceResponse = `
  mutation DeleteServiceResponse($input: DeleteSERVICE_RESPONSEInput!) {
    deleteSERVICE_RESPONSE(input: $input) {
      SERVICE_RESPONSE_ID
    }
  }
`

// Service Response Mapping mutations
export const createServiceResponseMapping = `
  mutation CreateServiceResponseMapping($input: CreateSERVICE_RESPONSE_MAPPINGInput!) {
    createSERVICE_RESPONSE_MAPPING(input: $input) {
      SERVICE_RESPONSE_MAPPING_ID
      SERVICE_RESPONSE_ID
      ORIGIN_PRODUCT_ID
      OUTPUT_PATH
    }
  }
`

export const updateServiceResponseMapping = `
  mutation UpdateServiceResponseMapping($input: UpdateSERVICE_RESPONSE_MAPPINGInput!) {
    updateSERVICE_RESPONSE_MAPPING(input: $input) {
      SERVICE_RESPONSE_MAPPING_ID
      SERVICE_RESPONSE_ID
      ORIGIN_PRODUCT_ID
      OUTPUT_PATH
    }
  }
`

export const deleteServiceResponseMapping = `
  mutation DeleteServiceResponseMapping($input: DeleteSERVICE_RESPONSE_MAPPINGInput!) {
    deleteSERVICE_RESPONSE_MAPPING(input: $input) {
      SERVICE_RESPONSE_MAPPING_ID
    }
  }
`

// Sort Criteria mutations
export const createSortCriteria = `
  mutation CreateSortCriteria($input: CreateSORT_CRITERIAInput!) {
    createSORT_CRITERIA(input: $input) {
      SORT_CRITERIA_ID
      ORIGIN_PRODUCT_ID
      STEP_TYPE_ID
      JSON_PATH
      SORT_ORDER
      SORT_PRIORITY
      SEQUENCE_NBR
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const updateSortCriteria = `
  mutation UpdateSortCriteria($input: UpdateSORT_CRITERIAInput!) {
    updateSORT_CRITERIA(input: $input) {
      SORT_CRITERIA_ID
      ORIGIN_PRODUCT_ID
      STEP_TYPE_ID
      JSON_PATH
      SORT_ORDER
      SORT_PRIORITY
      SEQUENCE_NBR
      CREATED_DATE
      CHANGED_DATE
    }
  }
`

export const deleteSortCriteria = `
  mutation DeleteSortCriteria($input: DeleteSORT_CRITERIAInput!) {
    deleteSORT_CRITERIA(input: $input) {
      SORT_CRITERIA_ID
    }
  }
`

// Step Service Mapping mutations
export const createStepServiceMapping = `
  mutation CreateStepServiceMapping($input: CreateSTEP_SERVICE_MAPPINGInput!) {
    createSTEP_SERVICE_MAPPING(input: $input) {
      STEP_SERVICE_MAPPING_ID
      STEP_TYPE_ID
      SERVICE_ID
      SEQUENCE_NBR
    }
  }
`

export const updateStepServiceMapping = `
  mutation UpdateStepServiceMapping($input: UpdateSTEP_SERVICE_MAPPINGInput!) {
    updateSTEP_SERVICE_MAPPING(input: $input) {
      STEP_SERVICE_MAPPING_ID
      STEP_TYPE_ID
      SERVICE_ID
      SEQUENCE_NBR
    }
  }
`

export const deleteStepServiceMapping = `
  mutation DeleteStepServiceMapping($input: DeleteSTEP_SERVICE_MAPPINGInput!) {
    deleteSTEP_SERVICE_MAPPING(input: $input) {
      STEP_SERVICE_MAPPING_ID
    }
  }
`

// Step Type mutations
export const createStepType = `
  mutation CreateStepType($input: CreateSTEP_TYPEInput!) {
    createSTEP_TYPE(input: $input) {
      STEP_TYPE_ID
      STEP_TYPE_NAME
      STEP_TYPE_DESC
      RESOURCE_NAME
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const updateStepType = `
  mutation UpdateStepType($input: UpdateSTEP_TYPEInput!) {
    updateSTEP_TYPE(input: $input) {
      STEP_TYPE_ID
      STEP_TYPE_NAME
      STEP_TYPE_DESC
      RESOURCE_NAME
      CREATED_BY_USER_ID
      CREATED_DATE
      CHANGED_BY_USER_ID
      CHANGED_DATE
    }
  }
`

export const deleteStepType = `
  mutation DeleteStepType($input: DeleteSTEP_TYPEInput!) {
    deleteSTEP_TYPE(input: $input) {
      STEP_TYPE_ID
    }
  }
`

// Step Type Param Map mutations
export const createStepTypeParamMap = `
  mutation CreateStepTypeParamMap($input: CreateSTEP_TYPE_PARAM_MAPInput!) {
    createSTEP_TYPE_PARAM_MAP(input: $input) {
      STEP_TYPE_PARAM_MAP_ID
      STEP_TYPE_ID
      SERVICE_PARAM_MAPPING_ID
      CREATED_BY_USER_ID
      CREATED_DATE
    }
  }
`

export const updateStepTypeParamMap = `
  mutation UpdateStepTypeParamMap($input: UpdateSTEP_TYPE_PARAM_MAPInput!) {
    updateSTEP_TYPE_PARAM_MAP(input: $input) {
      STEP_TYPE_PARAM_MAP_ID
      STEP_TYPE_ID
      SERVICE_PARAM_MAPPING_ID
      CREATED_BY_USER_ID
      CREATED_DATE
    }
  }
`

export const deleteStepTypeParamMap = `
  mutation DeleteStepTypeParamMap($input: DeleteSTEP_TYPE_PARAM_MAPInput!) {
    deleteSTEP_TYPE_PARAM_MAP(input: $input) {
      STEP_TYPE_PARAM_MAP_ID
    }
  }
`
